import { KeywordBase } from "@api/useGetKeywordBaseData";
import { useCallback, useMemo } from "react";
import { useSelectFilters } from "@vms/SelectFilters/SelectFiltersContext";
import {
    useFilterOutSelectedKeywords,
    useSelectedKeywordsContext
} from "@vms/SelectedKeywords/SelectedKeywordsContext";
import { includesInLC, startsFromInLC } from "@datassential/platform-ui-lib";

export function useFilteredKeywordsData<T extends KeywordBase>(
    rawData: T[],
    search: string = '',
) {
    const { getMatchFilters } = useSelectFilters();
    const { selectedKeywordsIds, isSelectedKeywordsView } = useSelectedKeywordsContext();

    const isSearchApplied = useMemo(
        (): boolean => search.trim().length > 0,
        [search]
    );

    const preSortedData = useMemo(
        (): T[] => [...rawData].sort(
            (a, b) => (a.name ?? '').localeCompare(b.name ?? '')
        ),
        [rawData]
    );

    useFilterOutSelectedKeywords(rawData);

    const filteredData = useMemo(
        (): T[] => preSortedData.filter(getMatchFilters),
        [preSortedData, getMatchFilters]
    );

    const selectedKeywords = useMemo(
        (): T[] => preSortedData.filter(i => selectedKeywordsIds.includes(i.id)),
        [preSortedData, selectedKeywordsIds]
    );

    const dataForSorting = useMemo(
        () => isSelectedKeywordsView ? selectedKeywords : filteredData,
        [isSelectedKeywordsView, selectedKeywords, filteredData]
    );

    const filteredDataBySearch = useMemo(
        () => {
            if (!isSearchApplied) return dataForSorting;

            return [
                ...dataForSorting.filter(i => startsFromInLC(i.name ?? '', search.trim())),
                ...dataForSorting.filter(i =>
                    !startsFromInLC(i.name ?? '', search.trim())
                    && includesInLC(i.name ?? '', search.trim())
                ),
            ];
        },
        [isSearchApplied, dataForSorting, search]
    );

    const getFinalData = useCallback(
        (sortedData: T[]) => {
            if (!isSearchApplied) return sortedData;

            return filteredDataBySearch;
        },
        [isSearchApplied, filteredDataBySearch]
    );

    return {
        preSortedData,
        filteredData,
        selectedKeywords,
        filteredDataBySearch,
        dataForSorting,
        getFinalData,
    };
}